import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, ArrowRight, Send, Plane, Star } from "lucide-react";
import tourData from "../assets/data/tours.js";

const safeTours = Array.isArray(tourData) ? tourData : [];

const TourCard = ({ tour, index, reduceMotion }) => {
  const cardReveal = reduceMotion
    ? {}
    : {
        initial: { opacity: 0, y: 30 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: '0px 0px -10% 0px' },
        transition: { duration: 0.6, delay: index * 0.05, ease: [0.16, 1, 0.3, 1] }
      };

  const tourId = tour.id || tour._id;
  const title = tour.title || 'Untitled Journey';
  const city = tour.city || tour.address || 'Various';
  const photo = tour.photo || '';
  const maxGroup = tour.maxGroupSize || null;

  return (
    <motion.article className="tour-card" {...cardReveal} whileHover={reduceMotion ? undefined : { y: -6 }}>
      <Link to={`/tours/${tourId}`} className="tour-card-link">
        <div className="tour-card-media">
          {photo ? (
            <img src={photo} alt={title} loading="lazy" />
          ) : (
            <div className="tour-card-placeholder">
              <Plane size={28} />
            </div>
          )}
          {maxGroup && (
            <div className="tour-card-overlay">
              <span className="tour-card-pill">
                <Star size={12} /> Max {maxGroup} people
              </span>
            </div>
          )}
        </div>
        <div className="tour-card-body">
          <h3 className="tour-card-title">{title}</h3>
          <p className="tour-card-location">
            <MapPin size={14} /> {city}
          </p>
          <div className="tour-card-footer">
            <span className="tour-card-price">Custom Itinerary</span>
            <span className="tour-card-cta">
              View <ArrowRight size={16} />
            </span>
          </div>
        </div>
      </Link>
    </motion.article>
  );
};

const Newsletter = ({ reduceMotion }) => {
  const [subscribed, setSubscribed] = useState(false);

  const reveal = reduceMotion
    ? {}
    : {
        initial: { opacity: 0, y: 22 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true },
        transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] }
      };

  return (
    <section className="section sectiona newsletter-section" aria-labelledby="newsletter-heading-tours">
      <div className="container narrow">
        <motion.div className="card newsletter-card" {...reveal}>
          <h2 id="newsletter-heading-tours" className="h2">Travel updates, occasionally</h2>
          <p>Destination notes, visa reminders, and planning tips. No noise.</p>

          {subscribed ? (
            <div className="success" role="status">Thanks — you're on the list.</div>
          ) : (
            <form
              className="newsletter-form"
              onSubmit={(event) => {
                event.preventDefault();
                event.currentTarget.reset();
                setSubscribed(true);
              }}
            >
              <label
                htmlFor="newsletter-email-tours"
                style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}
              >
                Email address
              </label>
              <input
                id="newsletter-email-tours"
                className="newsletter-input"
                type="email"
                name="email"
                placeholder="your@email.com"
                required
              />
              <button className="btn-primary" type="submit">
                <Send size={18} aria-hidden="true" />
                Subscribe
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};

const Tours = ({ isDarkMode = false }) => {
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    const prevBg = document.body.style.backgroundColor;
    const prevScheme = document.body.style.colorScheme;
    document.body.style.backgroundColor = isDarkMode ? '#061110' : '#F7FBFA';
    document.body.style.colorScheme = isDarkMode ? 'dark' : 'light';
    return () => {
      document.body.style.backgroundColor = prevBg;
      document.body.style.colorScheme = prevScheme;
    };
  }, [isDarkMode]);

  const heroReveal = reduceMotion
    ? {}
    : {
        initial: { opacity: 0, y: 26 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] }
      };

  const globalStyles = `
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Sora:wght@600;700&display=swap');

    .tours-root {
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      background: var(--bg); color: var(--text); min-height: 100vh;
      -webkit-font-smoothing: antialiased;
    }
    .tours-root * { box-sizing: border-box; }
    .tours-root h1, .tours-root h2, .tours-root h3 { font-family: 'Sora', sans-serif; color: var(--text); }
    .tours-root p { color: var(--text-muted); }

    .container { width: min(1180px, calc(100% - 2.5rem)); margin-inline: auto; }
    .container.narrow { max-width: 880px; }
    .section { padding-block: clamp(4rem, 8vw, 6.75rem); }
    .newsletter-section { padding-top: 0; }

    .hero { padding-top: clamp(8rem, 12vw, 10rem); text-align: center; margin-bottom: 2rem; }
    .sectiona{ margin-top: 4rem}

    .eyebrow {
      display: inline-flex; align-items: center; gap: 0.55rem;
      padding: 0.48rem 0.85rem; border-radius: 999px;
      border: 1px solid var(--border-strong); background: var(--surface);
      color: var(--primary); font-size: 0.78rem; font-weight: 700;
      letter-spacing: 0.14em; text-transform: uppercase;
    }

    .h1 {
      font-size: clamp(2.5rem, 5vw, 3.75rem); line-height: 1.1;
      letter-spacing: -0.03em; margin: 1.25rem 0 1.5rem; font-weight: 700;
    }
    .lead { font-size: 1.125rem; line-height: 1.7; color: var(--text-muted); max-width: 640px; margin: 0 auto; }
    .h2 { font-size: clamp(1.75rem, 3vw, 2.25rem); margin: 0 0 0.8rem; font-weight: 700; }

    .tours-grid { display: grid; gap: 1.5rem; grid-template-columns: 1fr; }
    @media (min-width: 640px) { .tours-grid { grid-template-columns: repeat(2, 1fr); } }
    @media (min-width: 1024px) { .tours-grid { grid-template-columns: repeat(3, 1fr); } }
    @media (min-width: 1280px) { .tours-grid { grid-template-columns: repeat(4, 1fr); } }

    .tour-card {
      background: var(--surface); border: 1px solid var(--border); border-radius: 24px;
      overflow: hidden; box-shadow: var(--shadow);
      transition: transform 0.3s ease, box-shadow 0.3s ease;
      display: flex; flex-direction: column; height: 100%;
    }
    .tour-card:hover { box-shadow: 0 24px 60px rgba(15, 118, 110, 0.15); }
    .tour-card-link { text-decoration: none; color: inherit; display: flex; flex-direction: column; height: 100%; }

    .tour-card-media { position: relative; aspect-ratio: 16 / 11; overflow: hidden; background: var(--bg-muted); }
    .tour-card-media img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.6s ease; }
    .tour-card:hover .tour-card-media img { transform: scale(1.05); }

    .tour-card-placeholder {
      width: 100%; height: 100%; display: grid; place-items: center;
      color: var(--text-muted); background: var(--bg-muted);
    }

    .tour-card-overlay { position: absolute; top: 1rem; left: 1rem; z-index: 2; }
    .tour-card-pill {
      display: inline-flex; align-items: center; gap: 0.4rem;
      background: rgba(5, 20, 18, 0.65); backdrop-filter: blur(8px);
      color: #fff; padding: 0.35rem 0.75rem; border-radius: 999px;
      font-size: 0.75rem; font-weight: 700; border: 1px solid rgba(255,255,255,0.15);
    }

    .tour-card-body { padding: 1.5rem; display: flex; flex-direction: column; gap: 0.75rem; flex: 1; }
    .tour-card-title { font-size: 1.15rem; font-weight: 700; margin: 0; line-height: 1.3; }
    .tour-card-location {
      display: flex; align-items: center; gap: 0.4rem;
      font-size: 0.875rem; color: var(--text-muted); margin: 0;
    }
    .tour-card-footer {
      margin-top: auto; padding-top: 1.25rem; border-top: 1px solid var(--border);
      display: flex; justify-content: space-between; align-items: center;
    }
    .tour-card-price { font-size: 0.85rem; font-weight: 600; color: var(--text-muted); }
    .tour-card-cta {
      display: flex; align-items: center; gap: 0.35rem;
      font-size: 0.9rem; font-weight: 700; color: var(--primary);
    }

    .card {
      background: var(--surface); border: 1px solid var(--border); border-radius: 24px;
      box-shadow: var(--shadow); backdrop-filter: blur(14px);
    }
    .newsletter-card {
      padding: clamp(2rem, 5vw, 3.5rem); text-align: center;
      background: linear-gradient(135deg, rgba(15, 118, 110, 0.16), rgba(124, 58, 237, 0.14));
      border-color: var(--border-strong);
    }
    .newsletter-card .h2 { margin-bottom: 0.6rem; }
    .newsletter-card > p { max-width: 56ch; margin: 0 auto 1.75rem; }
    .newsletter-form { display: grid; gap: 0.9rem; max-width: 560px; margin: 0 auto; grid-template-columns: 1fr auto; }
    @media (max-width: 600px) { .newsletter-form { grid-template-columns: 1fr; } }

    .newsletter-input {
      width: 100%; min-height: 54px; border-radius: 999px;
      border: 1px solid var(--border-strong); background: var(--surface);
      color: var(--text); padding: 0 1.35rem; font-size: 1rem; font-family: inherit; outline: none;
    }
    .newsletter-input:focus { border-color: var(--primary); box-shadow: 0 0 0 4px var(--focus); }
    .newsletter-input::placeholder { color: var(--text-muted); opacity: 0.75; }

    .btn-primary {
      min-height: 54px; border-radius: 999px; padding: 0 1.8rem;
      background: linear-gradient(135deg, var(--btn-from), var(--btn-to));
      color: #fff; font-size: 1rem; font-weight: 700; border: none; cursor: pointer;
      display: inline-flex; align-items: center; justify-content: center; gap: 0.6rem;
      box-shadow: 0 14px 32px rgba(15, 118, 110, 0.28); transition: all 0.25s ease;
      font-family: inherit;
    }
    .btn-primary:hover { transform: translateY(-2px); box-shadow: 0 18px 38px rgba(15, 118, 110, 0.36); }

    .success {
      padding: 1rem 1.25rem; border-radius: 16px; background: var(--surface);
      border: 1px solid var(--border-strong); color: var(--text); font-weight: 600;
      max-width: 560px; margin: 0 auto;
    }

    .empty-state { text-align: center; padding: 4rem 1rem; }
    .empty-state p { font-size: 1.1rem; margin: 0; }

    @media (max-width: 640px) { .container { width: calc(100% - 2rem); } }
  `;

  return (
    <main
      className="tours-root"
      style={{
        '--bg': isDarkMode ? '#061110' : '#F7FBFA',
        '--bg-muted': isDarkMode ? '#0A1917' : '#EFF7F5',
        '--surface': isDarkMode ? 'rgba(16, 38, 35, 0.72)' : '#FFFFFF',
        '--border': isDarkMode ? 'rgba(94, 234, 212, 0.14)' : 'rgba(15, 118, 110, 0.14)',
        '--border-strong': isDarkMode ? 'rgba(94, 234, 212, 0.26)' : 'rgba(15, 118, 110, 0.26)',
        '--text': isDarkMode ? '#E9F6F3' : '#132E29',
        '--text-muted': isDarkMode ? '#A8C5BE' : '#4A645E',
        '--primary': isDarkMode ? '#2DD4BF' : '#0F766E',
        '--btn-from': isDarkMode ? '#0D9488' : '#0F766E',
        '--btn-to': isDarkMode ? '#0F766E' : '#134E4A',
        '--focus': isDarkMode ? 'rgba(94, 234, 212, 0.25)' : 'rgba(15, 118, 110, 0.15)',
        '--shadow': isDarkMode ? '0 18px 50px rgba(0,0,0,0.42)' : '0 18px 50px rgba(15,118,110,0.10)'
      }}
    >
      <style>{globalStyles}</style>

      <section className="hero" aria-labelledby="tours-heading">
        <div className="container">
          <motion.div {...heroReveal}>
            <span className="eyebrow">
              <Plane size={14} aria-hidden="true" /> Curated Journeys
            </span>
            <h1 id="tours-heading" className="h1">Explore Our Tours</h1>
            <p className="lead">
              Selected itineraries we can help plan, price, and coordinate.
              Every journey is tailored to your specific requirements and travel style.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section" style={{ paddingTop: 0 }} aria-label="Tour listings">
        <div className="container">
          {safeTours.length > 0 ? (
            <div className="tours-grid">
              {safeTours.map((tour, index) => (
                <TourCard
                  key={String(tour.id || tour._id || index)}
                  tour={tour}
                  index={index}
                  reduceMotion={reduceMotion}
                />
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <p>New itineraries are currently being curated. Check back soon.</p>
            </div>
          )}
        </div>
      </section>

      <Newsletter reduceMotion={reduceMotion} />
    </main>
  );
};

export default Tours;































// import React, { useState, useEffect } from "react";
// import { Link, useNavigate } from "react-router-dom";
// import { motion, useReducedMotion } from "framer-motion";
// import { MapPin, Calendar, ArrowRight, Send, Plane, ChevronRight } from "lucide-react";
// import tourData from "../assets/data/tours.js";

// // Premium Tour Card Component
// const TourCard = ({ tour, isDarkMode, index }) => {
//   const reduceMotion = useReducedMotion();
  
//   const cardReveal = reduceMotion ? {} : {
//     initial: { opacity: 0, y: 30 },
//     whileInView: { opacity: 1, y: 0 },
//     viewport: { once: true, margin: '0px 0px -10% 0px' },
//     transition: { duration: 0.6, delay: index * 0.05, ease: [0.16, 1, 0.3, 1] }
//   };

//   return (
//     <motion.article className="tour-card" {...cardReveal} whileHover={{ y: -6 }}>
//       <Link to={`/tours/${tour.id || tour._id}`} className="tour-card-link">
//         <div className="tour-card-media">
//           <img src={tour.photo} alt={tour.title} loading="lazy" />
//           <div className="tour-card-overlay">
//              <span className="tour-card-pill">
//                <Calendar size={14} /> {tour.distance || '5'} Days
//              </span>
//           </div>
//         </div>
//         <div className="tour-card-body">
//           <h3 className="tour-card-title">{tour.title}</h3>
//           <p className="tour-card-location">
//             <MapPin size={14} /> {tour.city || tour.address}
//           </p>
//           <div className="tour-card-footer">
//             <span className="tour-card-price">Custom Itinerary</span>
//             <span className="tour-card-cta">
//               View <ArrowRight size={16} />
//             </span>
//           </div>
//         </div>
//       </Link>
//     </motion.article>
//   );
// };

// // Premium Newsletter Component (Matches Home Page)
// const Newsletter = ({ isDarkMode }) => {
//   const [subscribed, setSubscribed] = useState(false);
//   const reduceMotion = useReducedMotion();

//   const reveal = reduceMotion ? {} : {
//     initial: { opacity: 0, y: 22 },
//     whileInView: { opacity: 1, y: 0 },
//     viewport: { once: true },
//     transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] }
//   };

//   return (
//     <section className="section newsletter-section" aria-labelledby="newsletter-heading">
//       <div className="container narrow">
//         <motion.div className="card newsletter-card" {...reveal}>
//           <h2 id="newsletter-heading" className="h2">Travel updates, occasionally</h2>
//           <p>Destination notes, visa reminders, and planning tips.</p>

//           {subscribed ? (
//             <div className="success" role="status">Thanks — you’re on the list.</div>
//           ) : (
//             <form
//               className="newsletter-form"
//               onSubmit={event => {
//                 event.preventDefault();
//                 event.currentTarget.reset();
//                 setSubscribed(true);
//               }}
//             >
//               <label htmlFor="newsletter-email-tours" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
//                 Email address
//               </label>
//               <input
//                 id="newsletter-email-tours"
//                 className="newsletter-input"
//                 type="email"
//                 name="email"
//                 placeholder="your@email.com"
//                 required
//               />
//               <button className="btn-primary" type="submit">
//                 <Send size={18} aria-hidden="true" />
//                 Subscribe
//               </button>
//             </form>
//           )}
//         </motion.div>
//       </div>
//     </section>
//   );
// };

// const Tours = ({ isDarkMode = false }) => {
//   const reduceMotion = useReducedMotion();

//   useEffect(() => {
//     document.body.style.backgroundColor = isDarkMode ? '#061110' : '#F7FBFA';
//     document.body.style.colorScheme = isDarkMode ? 'dark' : 'light';
//     return () => {
//       document.body.style.backgroundColor = '';
//       document.body.style.colorScheme = '';
//     };
//   }, [isDarkMode]);

//   const heroReveal = reduceMotion ? {} : {
//     initial: { opacity: 0, y: 26 },
//     animate: { opacity: 1, y: 0 },
//     transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] }
//   };

//   const globalStyles = `
//     @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Sora:wght@600;700&display=swap');

//     .tours-root {
//       font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
//       background: var(--bg);
//       color: var(--text);
//       min-height: 100vh;
//       -webkit-font-smoothing: antialiased;
//     }
//     .tours-root * { box-sizing: border-box; }
//     .tours-root h1, .tours-root h2, .tours-root h3 { font-family: 'Sora', sans-serif; color: var(--text); }
//     .tours-root p { color: var(--text-muted); }

//     .container { width: min(1180px, calc(100% - 2.5rem)); margin-inline: auto; }
//     .container.narrow { max-width: 880px; }
//     .section { padding-block: clamp(4rem, 8vw, 6.75rem); }
//     .sectiona { margin-top: 5rem;}
//     .newsletter-section { padding-top: 0; }

//     .hero { padding-top: clamp(8rem, 12vw, 10rem); text-align: center; margin-bottom: 2rem; }
//     .eyebrow {
//       display: inline-flex; align-items: center; gap: 0.55rem;
//       padding: 0.48rem 0.85rem; border-radius: 999px;
//       border: 1px solid var(--border-strong); background: var(--surface);
//       color: var(--primary); font-size: 0.78rem; font-weight: 700;
//       letter-spacing: 0.14em; text-transform: uppercase;
//     }
//     .h1 { font-size: clamp(2.5rem, 5vw, 3.75rem); line-height: 1.1; letter-spacing: -0.03em; margin: 1.25rem 0 1.5rem; font-weight: 700; }
//     .lead { font-size: 1.125rem; line-height: 1.7; color: var(--text-muted); max-width: 640px; margin: 0 auto; }
//     .h2 { font-size: clamp(1.75rem, 3vw, 2.25rem); margin: 0 0 0.8rem; font-weight: 700; }

//     .tours-grid {
//       display: grid; gap: 1.5rem; grid-template-columns: 1fr;
//     }
//     @media (min-width: 640px) { .tours-grid { grid-template-columns: repeat(2, 1fr); } }
//     @media (min-width: 1024px) { .tours-grid { grid-template-columns: repeat(3, 1fr); } }
//     @media (min-width: 1280px) { .tours-grid { grid-template-columns: repeat(4, 1fr); } }

//     .tour-card {
//       background: var(--surface); border: 1px solid var(--border); border-radius: 24px;
//       overflow: hidden; box-shadow: var(--shadow); transition: transform 0.3s ease, box-shadow 0.3s ease;
//       display: flex; flex-direction: column; height: 100%;
//     }
//     .tour-card:hover { box-shadow: 0 24px 60px rgba(15, 118, 110, 0.15); }
    
//     .tour-card-link { text-decoration: none; color: inherit; display: flex; flex-direction: column; height: 100%; }

//     .tour-card-media {
//       position: relative; aspect-ratio: 16 / 11; overflow: hidden;
//     }
//     .tour-card-media img {
//       width: 100%; height: 100%; object-fit: cover; transition: transform 0.6s ease;
//     }
//     .tour-card:hover .tour-card-media img { transform: scale(1.05); }

//     .tour-card-overlay {
//       position: absolute; top: 1rem; left: 1rem; z-index: 2;
//     }
//     .tour-card-pill {
//       display: inline-flex; align-items: center; gap: 0.4rem;
//       background: rgba(5, 20, 18, 0.65); backdrop-filter: blur(8px);
//       color: #fff; padding: 0.35rem 0.75rem; border-radius: 999px;
//       font-size: 0.75rem; font-weight: 700; border: 1px solid rgba(255,255,255,0.15);
//     }

//     .tour-card-body {
//       padding: 1.5rem; display: flex; flex-direction: column; gap: 0.75rem; flex: 1;
//     }
//     .tour-card-title { font-size: 1.15rem; font-weight: 700; margin: 0; line-height: 1.3; }
//     .tour-card-location {
//       display: flex; align-items: center; gap: 0.4rem;
//       font-size: 0.875rem; color: var(--text-muted); margin: 0;
//     }
//     .tour-card-footer {
//       margin-top: auto; padding-top: 1.25rem; border-top: 1px solid var(--border);
//       display: flex; justify-content: space-between; align-items: center;
//     }
//     .tour-card-price { font-size: 0.85rem; font-weight: 600; color: var(--text-muted); }
//     .tour-card-cta {
//       display: flex; align-items: center; gap: 0.35rem;
//       font-size: 0.9rem; font-weight: 700; color: var(--primary);
//     }

//     /* Newsletter Styles matching Home */
//     .card {
//       background: var(--surface); border: 1px solid var(--border); border-radius: 24px;
//       box-shadow: var(--shadow); backdrop-filter: blur(14px);
//     }
//     .newsletter-card {
//       padding: clamp(2rem, 5vw, 3.5rem); text-align: center;
//       background: linear-gradient(135deg, rgba(15, 118, 110, 0.16), rgba(124, 58, 237, 0.14));
//       border-color: var(--border-strong);
//     }
//     .newsletter-card .h2 { margin-bottom: 0.6rem; }
//     .newsletter-card > p { max-width: 56ch; margin: 0 auto 1.75rem; }
//     .newsletter-form { display: grid; gap: 0.9rem; max-width: 560px; margin: 0 auto; grid-template-columns: 1fr auto; }
    
//     @media (max-width: 600px) { .newsletter-form { grid-template-columns: 1fr; } }

//     .newsletter-input {
//       width: 100%; min-height: 54px; border-radius: 999px;
//       border: 1px solid var(--border-strong); background: var(--surface);
//       color: var(--text); padding: 0 1.35rem; font-size: 1rem; font-family: inherit; outline: none;
//     }
//     .newsletter-input:focus { border-color: var(--primary); box-shadow: 0 0 0 4px var(--focus); }
//     .newsletter-input::placeholder { color: var(--text-muted); opacity: 0.75; }

//     .btn-primary {
//       min-height: 54px; border-radius: 999px; padding: 0 1.8rem;
//       background: linear-gradient(135deg, var(--btn-from), var(--btn-to));
//       color: #fff; font-size: 1rem; font-weight: 700; border: none; cursor: pointer;
//       display: inline-flex; align-items: center; justify-content: center; gap: 0.6rem;
//       box-shadow: 0 14px 32px rgba(15, 118, 110, 0.28); transition: all 0.25s ease;
//     }
//     .btn-primary:hover { transform: translateY(-2px); box-shadow: 0 18px 38px rgba(15, 118, 110, 0.36); }

//     .success {
//       padding: 1rem 1.25rem; border-radius: 16px; background: var(--surface);
//       border: 1px solid var(--border-strong); color: var(--text); font-weight: 600;
//       max-width: 560px; margin: 0 auto;
//     }

//     @media (max-width: 640px) { .container { width: calc(100% - 2rem); } }
//   `;

//   return (
//     <main
//       className="tours-root"
//       style={{
//         '--bg': isDarkMode ? '#061110' : '#F7FBFA',
//         '--bg-muted': isDarkMode ? '#0A1917' : '#EFF7F5',
//         '--surface': isDarkMode ? 'rgba(16, 38, 35, 0.72)' : '#FFFFFF',
//         '--border': isDarkMode ? 'rgba(94, 234, 212, 0.14)' : 'rgba(15, 118, 110, 0.14)',
//         '--border-strong': isDarkMode ? 'rgba(94, 234, 212, 0.26)' : 'rgba(15, 118, 110, 0.26)',
//         '--text': isDarkMode ? '#E9F6F3' : '#132E29',
//         '--text-muted': isDarkMode ? '#A8C5BE' : '#4A645E',
//         '--primary': isDarkMode ? '#2DD4BF' : '#0F766E',
//         '--btn-from': isDarkMode ? '#0D9488' : '#0F766E',
//         '--btn-to': isDarkMode ? '#0F766E' : '#134E4A',
//         '--focus': isDarkMode ? 'rgba(94, 234, 212, 0.25)' : 'rgba(15, 118, 110, 0.15)',
//         '--shadow': isDarkMode ? '0 18px 50px rgba(0, 0, 0, 0.42)' : '0 18px 50px rgba(15, 118, 110, 0.10)'
//       }}
//     >
//       <style>{globalStyles}</style>

//       <section className="hero sectiona">
//         <div className="container">
//           <motion.div {...heroReveal}>
//             <span className="eyebrow">
//               <Plane size={14} aria-hidden="true" /> Curated Journeys
//             </span>
//             <h1 className="h1">Explore Our Tours</h1>
//             <p className="lead">
//               Selected itineraries we can help plan, price, and coordinate. 
//               Every journey is tailored to your specific requirements and travel style.
//             </p>
//           </motion.div>
//         </div>
//       </section>

//       <section className="section" style={{ paddingTop: 0 }}>
//         <div className="container">
//           <div className="tours-grid">
//             {tourData?.map((tour, index) => (
//               <TourCard key={tour.id || tour._id} tour={tour} isDarkMode={isDarkMode} index={index} />
//             ))}
//           </div>
          
//           {(!tourData || tourData.length === 0) && (
//             <p style={{ textAlign: 'center', marginTop: '3rem', fontSize: '1.1rem' }}>
//               New itineraries are currently being curated. Check back soon.
//             </p>
//           )}
//         </div>
//       </section>

//       <Newsletter isDarkMode={isDarkMode} />
//     </main>
//   );
// };

// export default Tours;























// import React, { useState, useEffect } from "react";
// import CommonSection from "../shared/CommonSection.jsx";
// import { Container, Row, Col } from "reactstrap";

// import "../styles/tour.css";
// import tourData from "../assets/data/tours.js";
// import TourCard from "./../shared/TourCard.jsx";
// // import SearchBar from "./../shared/SearchBar.jsx";
// import NewsLetter from "./../shared/Newsletter.jsx";

// const Tours = () => {
//   const [pageCount, setPageCount] = useState(0);
//   const [page, setPage] = useState();

//   useEffect(() => {
//     const pages = Math.ceil(5 / 4); //later we will use backend data count
//     setPageCount(pages);
//   }, [page]);
//   return (
//     <>
//       <CommonSection  title={"All Tours"} className='common'/>
//       <section>
//         {/* <Container>
//           <Row>
//             <SearchBar />
//           </Row>
//         </Container> */}
//       </section>
//       <section className="pt-0">
//         <Container>
//           <Row>
//             {tourData?.map((tour) => (
//               <Col lg="3" md='6' sm='6' className="mb-4" key={tour.id}>
//                 <TourCard tour={tour} />
//               </Col>
//             ))}
//             <Col lg="12">
//               <div className="pagination d-flex align-items-center justify-content-center mt-4 gap-3">
//                 {[...Array(pageCount).keys()].map((number) => (
//                   <span
//                     key={number}
//                     onClick={() => setPage(number)}
//                     className={page === number ? "active__page" : ""}
//                   >
//                     {number + 1}
//                   </span>
//                 ))}
//               </div>
//             </Col>
//           </Row>
//         </Container>
//       </section>    
//       <NewsLetter /> create custom newsletter similar to home page.
//     </>
//   );
// };

// export default Tours;
